import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Archive,
  Clock,
  User,
  Sparkles,
  Sun,
  Moon,
  Search,
  Terminal,
  KeyRound,
} from "lucide-react";

import { useThemeStore } from "@/store/theme.store.ts";
import { PostApiService, type Post } from "@/api/post.api";

const isProd = import.meta.env.PROD;
const runtimeOrigin = typeof window !== "undefined" ? window.location.origin : "";

function getApiBaseUrl(): string {
  const rawUrl =
    import.meta.env.VITE_API_URL ||
    (isProd
      ? import.meta.env.VITE_PROD_BASE_URL || runtimeOrigin
      : import.meta.env.VITE_DEV_BASE_URL || "http://localhost:1337") ||
    runtimeOrigin ||
    "http://localhost:1337";

  const normalized = String(rawUrl).replace(/\/+$/, "");
  if (/\/api$/i.test(normalized)) {
    return normalized.slice(0, -4);
  }
  if (/\/parse$/i.test(normalized)) {
    return normalized.slice(0, -6);
  }
  return normalized;
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useThemeStore();
  const themeToggleRef = useRef<HTMLButtonElement | null>(null);
  const [showConsoleDialog, setShowConsoleDialog] = useState(false);
  const [consoleKey, setConsoleKey] = useState("");
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchSourcePosts, setSearchSourcePosts] = useState<Post[]>([]);

  // 导航菜单项
  const navItems = [
    { path: "/", label: "首页", icon: Home },
    { path: "/archive", label: "归档", icon: Archive },
    { path: "/timeline", label: "时间轴", icon: Clock },
    { path: "/about", label: "关于", icon: User },
    { path: "/lab", label: "实验室", icon: Sparkles },
  ];

  const isActivePath = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleOpenConsole = () => {
    setShowConsoleDialog(true);
    setConsoleKey("");
  };

  const handleToggleTheme = () => {
    const button = themeToggleRef.current;
    if (!button) {
      toggleTheme();
      return;
    }
    const rect = button.getBoundingClientRect();
    toggleTheme({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
  };

  const handleOpenSearch = () => {
    setShowSearchDialog(true);
    setSearchKeyword("");
    setDebouncedKeyword("");
  };

  const handleConfirmSearch = (keywordFromClick?: string) => {
    const keyword = (keywordFromClick ?? searchKeyword).trim();
    setShowSearchDialog(false);
    if (!keyword) return;
    navigate(`/archive?q=${encodeURIComponent(keyword)}`);
  };

  useEffect(() => {
    if (!showSearchDialog) return;
    let cancelled = false;
    setSearching(true);
    PostApiService.getRecentPosts(300)
      .then((list) => {
        if (!cancelled) {
          setSearchSourcePosts(Array.isArray(list) ? list : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSearchSourcePosts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSearching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [showSearchDialog]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedKeyword(searchKeyword.trim().toLowerCase());
    }, 260);
    return () => window.clearTimeout(timer);
  }, [searchKeyword]);

  const realtimeSearchResults = useMemo(() => {
    if (!debouncedKeyword) return [] as Post[];
    const filtered = searchSourcePosts.filter((post) => {
      const title = String(post.title || "").toLowerCase();
      const tags = Array.isArray(post.tags)
        ? post.tags
            .map((item: any) => (typeof item === "string" ? item : item?.name || ""))
            .join(" ")
            .toLowerCase()
        : "";
      const categories = Array.isArray(post.categories)
        ? post.categories.map((item: any) => item?.name || "").join(" ").toLowerCase()
        : "";
      return `${title} ${tags} ${categories}`.includes(debouncedKeyword);
    });
    return filtered.slice(0, 8);
  }, [debouncedKeyword, searchSourcePosts]);

  const handleConfirmConsole = async () => {
    const key = consoleKey.trim();
    if (!key) {
      setShowConsoleDialog(false);
      return;
    }

    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/admin/verify-key`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key }),
      });

      if (res.ok) {
        const result = await res.json().catch(() => null);
        if (result && result.code === 200 && result.data?.valid === true) {
          setShowConsoleDialog(false);
          setConsoleKey("");
          // 跳转到独立的后台管理项目（开发环境使用独立端口，生产使用 /admin 路径）
          const adminTarget =
            import.meta.env.VITE_ADMIN_URL ||
            (import.meta.env.DEV ? 'http://localhost:4173/' : '/admin/');
          window.location.href = adminTarget;
          return;
        }
      }
    } catch {
      // 忽略错误，不给出任何提示
    }

    // 验证失败：静默关闭并清空
      setShowConsoleDialog(false);
      setConsoleKey("");
  };

  return (
    <header className="sticky top-0 z-50 bg-[var(--bg-card)] border-b border-[var(--border-light)] backdrop-blur-sm">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="leading-tight">
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
                  羽归阁
                </span>
                <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em] text-blue-500/80">
                  YUGUIGE
                </span>
              </div>
              <p className="hidden sm:block text-[11px] text-[var(--text-secondary)]">
                写字 · 记事 · 拾光
              </p>
            </div>
          </Link>

          {/* 桌面导航 */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`
                  flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                  ${
                    isActivePath(path)
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                  }
                `}
              >
                <Icon size={16} />
                <span>{label}</span>
              </Link>
            ))}
          </nav>

          {/* 右侧操作区 */}
          <div className="flex items-center space-x-2">
            {/* 搜索按钮 */}
            <button
              onClick={handleOpenSearch}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              title="搜索"
            >
              <Search size={20} />
            </button>

            {/* 主题切换 */}
            <button
              ref={themeToggleRef}
              onClick={handleToggleTheme}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              title={theme === "light" ? "切换到深色模式" : "切换到浅色模式"}
            >
              {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            {/* 控制台入口（桌面端，图标按钮） */}
            <button
              onClick={handleOpenConsole}
              className="hidden md:inline-flex p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              title="控制台"
            >
              <Terminal size={20} />
            </button>

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition-colors"
              title="菜单"
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

      </div>

      {/* 移动端导航菜单：固定定位，不参与文档流，避免顶开页面内容 */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 z-[60]">
          <button
            type="button"
            className="absolute inset-0 h-[calc(100vh-4rem)] w-full bg-black/35"
            onClick={() => setIsMenuOpen(false)}
            aria-label="关闭菜单"
          />
          <div className="relative border-b border-[var(--border-light)] bg-[var(--bg-card)] shadow-xl animate-slideInDown">
            <nav className="py-2">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`
                    flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors
                    ${
                      isActivePath(path)
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                    }
                  `}
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* 控制台密钥输入弹窗（无文案提示） */}
      {showConsoleDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center w-[100vw] h-[100vh] bg-black/40 overflow-hidden">
          <div className="bg-[var(--bg-card)] border border-[var(--border-light)] rounded-xl shadow-lg w-full max-w-xs p-4">
            <div className="relative">
              <input
                type="password"
                value={consoleKey}
                onChange={(e) => {
                  setConsoleKey(e.target.value);
                }}
                className="w-full pl-3 pr-9 py-2 text-sm border border-[var(--border-light)] rounded-lg bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder=""
                autoFocus
              />
              <KeyRound
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none"
              />
            </div>
            <div className="mt-3 flex justify-end gap-2 text-xs">
              <button
                onClick={() => {
                  setShowConsoleDialog(false);
                  setConsoleKey("");
                }}
                className="px-3 py-1.5 rounded-lg border border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                取消
              </button>
              <button
                onClick={handleConfirmConsole}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}

      {showSearchDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center w-[100vw] h-[100vh] bg-black/40 overflow-hidden px-3 sm:px-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border-light)] rounded-none shadow-lg w-full max-w-[680px] p-3 sm:p-4 max-h-[86vh] overflow-hidden">
            <div className="relative">
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleConfirmSearch();
                  }
                }}
                className="w-full pl-3 pr-9 py-2 text-sm border border-[var(--border-light)] rounded-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="搜索文章标题或标签"
                autoFocus
              />
              <Search
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] pointer-events-none"
              />
            </div>

            <div className="mt-3 max-h-[48vh] overflow-y-auto border border-[var(--border-light)] rounded-none">
              {searching ? (
                <div className="px-3 py-2 text-xs text-[var(--text-secondary)]">正在加载搜索索引...</div>
              ) : debouncedKeyword ? (
                realtimeSearchResults.length > 0 ? (
                  realtimeSearchResults.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => {
                        setShowSearchDialog(false);
                        navigate(`/post/${post.slug || post.id}`);
                      }}
                      className="w-full text-left px-3 py-2.5 border-b border-[var(--border-light)] last:border-b-0 hover:bg-gray-100/80 dark:hover:bg-gray-800/70 transition-colors"
                    >
                      <div className="text-sm text-[var(--text-primary)] truncate">{post.title || "未命名文章"}</div>
                      <div className="text-xs text-[var(--text-secondary)] truncate">
                        {(post.categories || []).map((c) => c?.name).filter(Boolean).join(" / ") || "未分类"}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-[var(--text-secondary)]">没有匹配结果</div>
                )
              ) : (
                <div className="px-3 py-2 text-xs text-[var(--text-secondary)]">输入关键词后展示实时结果</div>
              )}
            </div>

            <div className="mt-3 flex justify-end gap-2 text-xs">
              <button
                onClick={() => {
                  setShowSearchDialog(false);
                  setSearchKeyword("");
                  setDebouncedKeyword("");
                }}
                className="px-3 py-1.5 rounded-none border border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                取消
              </button>
              <button
                onClick={() => handleConfirmSearch()}
                className="px-3 py-1.5 rounded-none bg-blue-600 text-white hover:bg-blue-700"
              >
                搜索
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
